import { Link } from "@tanstack/react-router"
import { useEffect, useState } from "react"

import { cn } from "@/lib/utils"

import { BillboardLayout } from "./billboard"
import { CoachCardLayout } from "./coach-card"
import { PosterLayout } from "./poster"
import type { StartLayoutProps } from "./shared"
import { SplitLayout } from "./split"
import { CdNlLayout } from "./cd-nl"

export type { StartLayoutProps } from "./shared"

/** The poster once more, with the abstract picture family instead of photographs. */
function AbstractLayout(props: StartLayoutProps) {
  return <PosterLayout {...props} set="abstrakt" />
}

/**
 * The start page drafts for the design decision. Reached via `/?layout=…`;
 * without the parameter the page stays as it is. Once a direction is chosen,
 * the winner becomes the page and the rest of this folder goes.
 */
export const START_LAYOUTS = [
  { key: "billboard", label: "Billboard", component: BillboardLayout },
  { key: "geteilt", label: "Geteilt", component: SplitLayout },
  { key: "poster", label: "Poster", component: PosterLayout },
  { key: "karte", label: "Coach-Karte", component: CoachCardLayout },
  { key: "abstrakt", label: "Abstrakt", component: AbstractLayout },
  { key: "cd-nl", label: "CD NL", component: CdNlLayout },
] as const

export type StartLayoutKey = (typeof START_LAYOUTS)[number]["key"]

export function isStartLayoutKey(value: unknown): value is StartLayoutKey {
  return START_LAYOUTS.some((l) => l.key === value)
}

export function StartLayout({
  layout,
  ...props
}: StartLayoutProps & { layout: StartLayoutKey }) {
  const Component = START_LAYOUTS.find((l) => l.key === layout)!.component
  return <Component {...props} />
}

const SWITCHER_KEY = "nl.startLayoutSwitcher"

/**
 * Floats at the bottom so the drafts can be flipped through in a meeting.
 * Cmd/Ctrl + . hides and shows it, so it stays out of screenshots and demos;
 * the choice survives a reload.
 */
export function StartLayoutSwitcher({ active }: { active?: StartLayoutKey }) {
  const [visible, setVisible] = useState(() => {
    try {
      return localStorage.getItem(SWITCHER_KEY) !== "hidden"
    } catch {
      return true
    }
  })

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === ".") {
        e.preventDefault()
        setVisible((v) => {
          try {
            localStorage.setItem(SWITCHER_KEY, v ? "hidden" : "shown")
          } catch {
            // Storage may be blocked; the toggle still works for this page.
          }
          return !v
        })
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  if (!visible) return null

  return (
    <nav
      aria-label="Entwürfe"
      className="fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-1 rounded-full border border-[var(--rule)] bg-[var(--paper)]/90 p-1 shadow-[0_12px_40px_rgba(31,31,31,0.18)] backdrop-blur-xl"
    >
      {START_LAYOUTS.map((l) => (
        <Link
          key={l.key}
          to="/"
          search={{ layout: l.key }}
          replace
          className={cn(
            "rounded-full px-3 py-1.5 text-[12.5px] font-medium transition-colors",
            l.key === active
              ? "bg-foreground text-[var(--paper)]"
              : "text-muted-foreground hover:bg-muted hover:text-foreground"
          )}
        >
          {l.label}
        </Link>
      ))}
      <Link
        to="/"
        search={{}}
        replace
        className={cn(
          "rounded-full px-3 py-1.5 text-[12.5px] transition-colors",
          active
            ? "text-muted-foreground hover:bg-muted hover:text-foreground"
            : "bg-foreground font-medium text-[var(--paper)]"
        )}
      >
        Aktuell
      </Link>
    </nav>
  )
}
